import "datatables.net-dt/css/dataTables.dataTables.min.css";

import { getBasinStates, getQueryArgs } from "@js/nwcc-navbar.js";
import { renderGrowl } from "@js/nwcc-growl.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { fetchBasinData, getBasinData, getRefData } from "../model/get-data.js";
import { createBasinTables } from "./basin-reports-tables.js";
import * as utils from "./basin-reports-utils.js";

const basinStates = getBasinStates();

export const startYear = 1980;
export const currDate = new Date();
export const currMonth = currDate.getMonth() + 1;
export const currYear = currDate.getFullYear();

window.onload = windowOnLoad;

export function windowOnLoad() {
    setArgDetailsData(getPageArgs());
    loadMainNwcc();
    setUpInitialPage();
}

export function setUpInitialPage() {
    const queryArgs = getQueryArgs();
    addDisplayStyleListeners(queryArgs);
    addPrimaryForecastListeners(queryArgs);
    addForecastNormalsListeners(queryArgs);
    addHideNullStationsListeners(queryArgs);
    populateDropDowns(queryArgs);
    addReportToggleListeners();
    setInitialReportToggles(queryArgs);
    addMakeReportButtonListener();
    installSearchParamListeners();
}

export function getPageArgs() {
    return [
        {
            argName: "state",
            argDesc: "Selects the specified state the in the 'State' drop-down. Must be a 2-letter abbreviation.",
            argExample: "UT",
            elemId: "state-dd",
        },
        {
            argName: "month",
            argDesc: "Selects the specified month in the 'Month' drop-down. Must be a number between 1 and 12",
            argExample: "5",
            elemId: "month-dd",
        },
        {
            argName: "year",
            argDesc: "Selects the specified year in the 'Year' drop-down.",
            argExample: "2023",
            elemId: "year-dd",
        },
        {
            argName: "basin",
            argDesc:
                "Specify the basin to show data for. This should match the value in the 'Reports - Basins' drop-down.",
            argExample: "Little Cottonwood",
            elemId: "basin-dd",
        },
        {
            argName: "hideReports",
            argDesc: "Comma separated list of elements to hide from the overall report. Note: no spaces allowed.",
            argExample: "fcst, res",
            argOptions: ["snow", "prec", "fcst", "res"],
            elemName: "options--show-report",
        },
        {
            argName: "display",
            argDesc: "Specify whether tables should contain a scroll bar.",
            argExample: "scroll",
            argOptions: ["all", "scroll"],
            argDefault: "all",
            elemName: "options--display",
        },
        {
            argName: "fcstNormal",
            argDesc: "Specify which central tendency to use for streamflow forecast data.",
            argExample: "average",
            argOptions: ["median", "average"],
            argDefault: "median",
            elemName: "options--fcst-normals",
        },
        {
            argName: "hideNullStations",
            argDesc: "Hide stations that have no current data.",
            argExample: "true",
            argOptions: ["true", "false"],
            argDefault: "false",
            elemId: "switch--hide-missing-data",
        },
        {
            argName: "primaryForecastsOnly",
            argDesc: "Hide all forecasts that are not a station's primary forecast period.",
            argExample: "true",
            argOptions: ["true", "false"],
            argDefault: "false",
            elemId: "switch--only-primary-forecasts",
        },
    ];
}

export async function installSearchParamListeners() {
    const pageArgs = getPageArgs();
    const idArgs = pageArgs.filter((item) => {
        return item.hasOwnProperty("elemId");
    });
    const nameArgs = pageArgs.filter((item) => {
        return item.hasOwnProperty("elemName");
    });
    idArgs.forEach((arg) => {
        const id = arg.elemId;
        const element = document.getElementById(id);
        if (element.tagName.toLowerCase() === "select") {
            element.addEventListener("change", function () {
                updateSelectArg(this, arg);
            });
        }
        if (element.tagName.toLowerCase() === "input") {
            element.addEventListener("click", function () {
                updateInputArg(this, arg);
            });
        }
    });
    nameArgs.forEach((arg) => {
        const name = arg.elemName;
        const options = document.querySelectorAll(`input[name="${name}"]`);
        options.forEach((option) => {
            if (option.type === "radio") {
                option.addEventListener("click", function () {
                    updateRadioArg(this, arg);
                });
            }
            if (option.type === "checkbox") {
                option.addEventListener("click", function () {
                    updateCheckboxArg(this, arg);
                });
            }
        });
    });
}

export function updateSelectArg(element, arg) {
    const queryArgs = getQueryArgs();
    const argKey = arg?.argName;
    const argVal = element?.value?.toLowerCase();
    if (argVal) {
        queryArgs[argKey] = argVal;
        utils.updateSearchParams(queryArgs);
    }
}

export function updateInputArg(element, arg) {
    const queryArgs = getQueryArgs();
    const argKey = arg.argName;
    const argVal = element.checked ? "true" : "false";
    if (argVal) {
        queryArgs[argKey] = argVal;
        utils.updateSearchParams(queryArgs);
    }
}

export function updateRadioArg(element, arg) {
    if (element.checked) {
        const queryArgs = getQueryArgs();
        const argKey = arg.argName;
        const id = element.id;
        const argVal = id.split("--")[1];
        if (argVal) {
            queryArgs[argKey] = argVal;
            utils.updateSearchParams(queryArgs);
        }
    }
}

export function updateCheckboxArg(element, arg) {
    const queryArgs = getQueryArgs();
    const argKey = arg.argName;
    const id = element.id;
    const argVal = id.split("--")[1];
    let argArr = queryArgs?.[argKey] ? queryArgs[argKey].split(",") : [];
    if (!argArr.includes(argVal) && !element.checked) {
        argArr.push(argVal);
    }
    if (argArr.includes(argVal) && element.checked) {
        argArr = argArr.filter((i) => {
            return i !== argVal;
        });
    }
    if (argArr.length) {
        queryArgs[argKey] = argArr.join(",");
    } else {
        delete queryArgs[argKey];
    }
    utils.updateSearchParams(queryArgs);
}

export function populateDropDowns(queryArgs = {}) {
    const stateQuery = queryArgs?.state?.toLowerCase() || "AK";
    const monthQuery = queryArgs?.month?.toLowerCase() || currMonth;
    const yearQuery = queryArgs?.year?.toLowerCase() || currYear;
    const stateSelect = document.getElementById("state-dd");
    stateSelect.innerHTML = "";
    const basinAbbrvs = [];
    basinStates.forEach((stateObj) => {
        basinAbbrvs.push(stateObj.abbr);
        const opt = document.createElement("option");
        opt.value = stateObj.abbr;
        opt.innerHTML = stateObj.name;
        stateSelect.appendChild(opt);
        if (opt.value.toLowerCase() == stateQuery) {
            opt.selected = true;
        }
    });
    const monthSelect = document.getElementById("month-dd");
    monthSelect.innerHTML = "";
    utils.monthNames.forEach((month, idx) => {
        const opt = document.createElement("option");
        opt.value = idx + 1;
        opt.innerHTML = utils.monthLongNames[idx];
        monthSelect.appendChild(opt);
        if (opt.value.toLowerCase() == monthQuery) {
            opt.selected = true;
        }
    });
    const yearSelect = document.getElementById("year-dd");
    yearSelect.innerHTML = "";
    const validYears = Array(currYear - startYear + 1)
        .fill()
        .map((_, idx) => startYear + idx)
        .reverse();
    validYears.forEach((year) => {
        const opt = document.createElement("option");
        opt.value = year;
        opt.innerHTML = year;
        yearSelect.appendChild(opt);
        if (opt.value.toLowerCase() == yearQuery) {
            opt.selected = true;
        }
    });
    if (basinAbbrvs.includes(queryArgs?.state?.toUpperCase() || "no state")) {
        utils.clearReports();
        utils.clearBasins();
        populateBasinOptions();
    }
}

export function addMakeReportButtonListener() {
    const makeReportBtn = document.getElementById("make-report-btn");
    makeReportBtn.addEventListener("click", (event) => {
        utils.clearReports();
        utils.clearBasins();
        populateBasinOptions();
    });
}

export function addReportToggleListeners() {
    utils.reportElements.forEach((reportId) => {
        const toggle = document.getElementById(`show-report--${reportId}`);
        utils.setReportToggles(reportId, true);
        toggle.addEventListener("click", (event) => {
            utils.toggleReport(event.target);
            utils.adjustHeaders();
        });
    });
}

export function setInitialReportToggles(queryArgs) {
    utils.reportElements.forEach((reportType) => {
        const toggle = document.getElementById(`show-report--${reportType}`);
        if (queryArgs?.hideReports) {
            const hideReports = queryArgs.hideReports.split(",");
            hideReports.map((rpt) => {
                return rpt.trim().toLowerCase();
            });
            if (hideReports.includes(reportType)) {
                toggle.checked = false;
            }
        }
    });
}

export function addPrimaryForecastListeners(queryArgs) {
    const primaryForecastsRadio = document.getElementById("switch--only-primary-forecasts");
    const primaryForecastsOnly = queryArgs?.primaryForecastsOnly || false;
    primaryForecastsRadio.checked = primaryForecastsOnly;
    primaryForecastsRadio.addEventListener("click", (event) => {
        if (Object.keys(getBasinData()).length > 0) {
            const basinSelect = document.getElementById("basin-dd");
            const basinType = basinSelect.selectedOptions[0].getAttribute("data-basintype");
            const selectedBasin = basinSelect.value;
            createBasinTables(selectedBasin, basinType);
            utils.adjustHeaders();
        }
    });
}

export function addDisplayStyleListeners(queryArgs) {
    const displayOptionsRadios = document.querySelectorAll('input[name="options--display"]');
    for (const radioButton of displayOptionsRadios) {
        const displayStyle = queryArgs?.display ? queryArgs.display.toLowerCase() : "all";
        const selectedDisplayStyle = radioButton.id.split("--")[1];
        radioButton.checked = displayStyle === selectedDisplayStyle ? true : false;
        radioButton.addEventListener("click", (event) => {
            const basinSelect = document.getElementById("basin-dd");
            const basinType = basinSelect.selectedOptions[0].getAttribute("data-basintype");
            const selectedBasin = basinSelect.value;
            createBasinTables(selectedBasin, basinType);
            utils.adjustHeaders();
        });
    }
}

export function addForecastNormalsListeners(queryArgs) {
    const fcstNormalsOptionsRadios = document.querySelectorAll('input[name="options--fcst-normals"]');
    for (const radioButton of fcstNormalsOptionsRadios) {
        let fcstNormal = queryArgs?.fcstNormal ? queryArgs.fcstNormal.toLowerCase() : "median";
        fcstNormal = fcstNormal === "average" ? "average" : "median";
        const selectedfcstNormal = radioButton.id.split("--")[1];
        radioButton.checked = fcstNormal === selectedfcstNormal ? true : false;
        radioButton.addEventListener("click", (event) => {
            if (document.getElementById("fcst").innerHTML !== "") {
                const basinSelect = document.getElementById("basin-dd");
                const basinType = basinSelect.selectedOptions[0].getAttribute("data-basintype");
                const selectedBasin = basinSelect.value;
                createBasinTables(selectedBasin, basinType);
                utils.adjustHeaders();
            }
        });
    }
}

export function addHideNullStationsListeners(queryArgs) {
    const hideNullStationsRadio = document.getElementById("switch--hide-missing-data");
    const hideNullStations = queryArgs?.hideNullStations ? queryArgs.hideNullStations : false;
    hideNullStationsRadio.checked = hideNullStations;
    hideNullStationsRadio.addEventListener("click", (event) => {
        if (Object.keys(getBasinData()).length > 0) {
            const basinSelect = document.getElementById("basin-dd");
            const basinType = basinSelect.selectedOptions[0].getAttribute("data-basintype");
            const selectedBasin = basinSelect.value;
            createBasinTables(selectedBasin, basinType);
            utils.adjustHeaders();
        }
    });
}

export function addBasinSelectListener() {
    const basinSelect = document.getElementById("basin-dd");
    basinSelect.addEventListener("change", (event) => {
        const selectedBasin = event.target.selectedOptions[0].value;
        const basinType = event.target.selectedOptions[0].getAttribute("data-basintype");
        createBasinTables(selectedBasin, basinType);
        utils.adjustHeaders();
    });
}

export async function populateBasinOptions(isValidDateOveride, parentsOveride) {
    const isValidDate = isValidDateOveride === undefined ? utils.checkPubDate() : isValidDateOveride;
    if (!isValidDate) {
        renderGrowl(
            "Invalid Date!",
            "Cannot create a report for a date in the future, please select an appropriate month and year.",
            "error",
            true,
            10,
        );
        return;
    }
    const queryArgs = getQueryArgs();
    utils.showLoadingBar(true);
    utils.showTables(false);
    await fetchBasinData();
    const parents = parentsOveride === undefined ? await getRefData("parents") : parentsOveride;
    const basinSelect = document.getElementById("basin-dd");
    let basinCnt = 0;
    let allBasins = [];
    Object.keys(parents).forEach((majorBasin) => {
        allBasins.push(majorBasin.toLowerCase());
        const minorBasins = parents[majorBasin];
        basinCnt = basinCnt + 1 + minorBasins.length;
        const basinOptGrp = document.createElement("option");
        basinOptGrp.text = majorBasin;
        basinOptGrp.className = basinOptGrp.className + "option-grp";
        basinOptGrp.value = majorBasin.toLowerCase();
        basinOptGrp.setAttribute("data-basintype", "major");
        basinSelect.appendChild(basinOptGrp);
        minorBasins.forEach(function (minor) {
            allBasins.push(minor.toLowerCase());
            const option = document.createElement("option");
            option.value = minor.toLowerCase();
            option.text = "    " + minor;
            const btype = majorBasin == "Misc. Basins" ? "misc" : "minor";
            option.setAttribute("data-basintype", btype);
            basinSelect.appendChild(option);
        });
    });
    const basinBadge = document.getElementById("basin-badge");
    basinBadge.innerHTML = basinCnt;
    utils.showTables(true);
    if (basinSelect.options.length > 0) {
        const querySelectedBasin = queryArgs.basin?.toLowerCase() || basinSelect.options[0].value;
        if (allBasins.includes(querySelectedBasin)) {
            basinSelect.value = querySelectedBasin;
        }
        const selectedBasin = basinSelect.selectedOptions[0].value;
        const basinType = basinSelect.selectedOptions[0].getAttribute("data-basintype");
        createBasinTables(selectedBasin, basinType);
        addBasinSelectListener();
    }
    utils.showLoadingBar(false);
}
